import React, { memo, useEffect } from "react";
import { compose } from "redux";
import { connect, useSelector } from "react-redux";
import { createStructuredSelector } from "reselect";
import { IntlProvider } from "react-intl";
import get from "lodash/get";
import {
  fetchReferralCode,
  fetchAppSettings,
  fetchLocale,
  fetchEligiblePlans,
  fetchCustomerInfo,
  fetchPhones,
  changeFormField,
  selectPlan,
  setData
} from "ducks/actions";
import {
  makeSelectMessages,
  makeSelectIsFetchingRequired,
  makeSelectRequiresMaintenance,
  makeSelectReferralCode,
  makeSelectCustomerInfo,
  makeSelectPlanName,
  makeSelectPhones,
  makeSelectEligiblePlans,
  makeSelectPhonesForm,
  makeSelectChannelPhonePaymentOptions,
  makeSelectWorkflowState,
  makeSelectRedirects
} from "ducks/selectors";
import {
  PHONE_ONLY_CHANNEL,
  COMBO_CHANNEL,
  DEFAULT_CHANNEL,
  DEFAULT_KEYS_TO_FETCH,
  forms,
  paths,
  USE_PHONE_WALLET_SOURCE_PARAMS
} from "utils/constants";
import {
  urlParam,
  upper,
  gotoUrl,
  getChannelFromUrl,
  getPaymentOption,
  getPaymentOptionFromUrl,
  getPaymentTermFromUrl,
  getProductIdFromUrl,
  getPhoneFilterFromUrl,
  getPlanFromUrl
} from "utils/helpers";
import { getIccIdInUrlParam, setIccIdInUrlParam } from "utils/localStorage";
import isEmpty from "lodash/isEmpty";
import find from "lodash/find";
import round from "lodash/round";
import includes from "lodash/includes";
import has from "lodash/has";
import { makeSelectAllowAccountNoQueryParam } from "ducks/selectors/appSettings";
import { getWorkflow } from "utils/workflow";
import { setWorkflow } from "ducks/actions/workflowActions";

export const DataLoader = props => {
  const {
    match,
    requiresFetching,
    render,
    messages,
    fetchData,
    referralCode,
    validateReferralCode,
    customerInfo,
    fetchCustomerInfo,
    fetchPhones,
    initWorkflow,
    planName,
    phones = [],
    selectPlan,
    setSelectedPhone,
    eligiblePlans,
    phonesForm = {},
    requiresMaintenance,
    channelPhonePaymentOptions,
    allowAccountNoQueryParam,
    workflowState,
    setIccidParam
  } = props;
  const pvid = getProductIdFromUrl();
  const paymentTerm = getPaymentTermFromUrl();
  const paymentOption = getPaymentOptionFromUrl();
  const selectedPlanName = getPlanFromUrl();
  const phoneFilter = getPhoneFilterFromUrl();
  const channel = getChannelFromUrl() || DEFAULT_CHANNEL;
  const hasPhones = phones?.length;
  const selectedPaymentTerm = phonesForm?.paymentTerm;
  const selectedPhoneVariant = phonesForm?.phoneVariant?.id;
  const phonePaymentOptions = get(channelPhonePaymentOptions, [channel], {});
  const shouldFetchCustomerInfo = includes(
    [COMBO_CHANNEL, PHONE_ONLY_CHANNEL],
    channel
  );
  const redirects = useSelector(makeSelectRedirects());

  useEffect(() => {
    if (urlParam("iccid")) {
      setIccIdInUrlParam();
      setIccidParam(true);
    } else if (getIccIdInUrlParam()) {
      setIccidParam(true);
    }
  }, [setIccidParam]);

  useEffect(() => {
    const initData = async () => {
      const workflow = await getWorkflow(channel, redirects);
      if (!isEmpty(workflow) && !has(workflowState, channel))
        initWorkflow(channel, workflow);
    };

    initData();
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel]);

  useEffect(
    () => {
      if (requiresFetching || urlParam("relaunch")) {
        let params = get(match, "params");
        if (!get(params, "channel")) params = { channel: DEFAULT_CHANNEL };

        fetchData(params);

        if (channel === COMBO_CHANNEL || channel === PHONE_ONLY_CHANNEL) {
          fetchPhones();
        }
      }
      if (shouldFetchCustomerInfo) {
        const account_number = allowAccountNoQueryParam
          ? upper(urlParam("account_no") || urlParam("accountNo"))
          : null;

        const ott = urlParam("ott");
        const source = urlParam("source") || urlParam("id");
        if (ott && source && channel === PHONE_ONLY_CHANNEL) {
          gotoUrl(`/phoneonly/phone-details?id=${source}`);
        }

        const use_phone_wallet = includes(
          USE_PHONE_WALLET_SOURCE_PARAMS,
          source
        );
        if (
          (account_number &&
            account_number !== get(customerInfo, "account_number")) ||
          (ott && isEmpty(customerInfo))
        ) {
          fetchCustomerInfo(account_number, ott, use_phone_wallet);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allowAccountNoQueryParam]
  );

  // validateReferralCode on change of planName
  useEffect(
    () => {
      // the authentication url check is to avoid considering code url params in social media redirection
      const isAuthenticationRedirection = window.location.pathname.includes(
        "authentication"
      );
      const referralCodeParam = !isAuthenticationRedirection
        ? upper(urlParam("code") || urlParam("referral"))
        : "";
      const appliedReferralCode = get(referralCode, "value");
      // give priority to appliedReferralCode
      const code = appliedReferralCode || referralCodeParam;

      const myInfoCondition = !isEmpty(code) && !isEmpty(urlParam("state")); //myinfo redirects with ?code=aaa&state=bbb . We treat the presence of these 2 params tgt as myInfo condition.

      if (
        code &&
        code !== appliedReferralCode &&
        planName &&
        !myInfoCondition
      ) {
        validateReferralCode(code, planName, pvid);
        // Remove promocode query param, once it is applied.
        if (referralCodeParam) {
          gotoUrl(window.location.pathname, true, {
            code: null,
            referral: null,
            reset: null
          });
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [planName]
  );

  useEffect(() => {
    if (
      [PHONE_ONLY_CHANNEL, COMBO_CHANNEL].includes(channel) &&
      !isEmpty(phones)
    ) {
      if (channel === COMBO_CHANNEL && selectedPlanName !== planName) {
        const selectedPlan = find(eligiblePlans, { name: selectedPlanName });
        selectedPlan && selectPlan(selectedPlan);
      }
      if (phoneFilter) {
        setSelectedPhone("filter_criteria", [
          {
            attribute: "name",
            category: "brand",
            label: phoneFilter,
            test: "includes",
            value: phoneFilter
          }
        ]);
      }
      if (pvid) {
        let allPhoneVariants = [];
        phones.forEach(phone => {
          if (
            !phone.outOfStock &&
            !phone.comingSoon &&
            !isEmpty(phone.allPhoneVariants)
          ) {
            allPhoneVariants = [...allPhoneVariants, ...phone.allPhoneVariants];
          }
        });
        const getPhoneVariant = find(allPhoneVariants, { id: pvid });
        if (getPhoneVariant) {
          const paymentType = getPaymentOption(
            paymentOption,
            channelPhonePaymentOptions,
            getPhoneVariant
          );
          setSelectedPhone("phoneVariant", getPhoneVariant);
          setSelectedPhone("phoneColorCode", getPhoneVariant.colorCode);
          setSelectedPhone("phoneColorName", getPhoneVariant.colorName);
          setSelectedPhone("phoneCapacity", getPhoneVariant.capacity);
          setSelectedPhone("phonePaymentOption", paymentType);

          if (
            paymentType === "installment" &&
            phonePaymentOptions?.installment
          ) {
            const minInstallmentMonth =
              getPhoneVariant?.minInstallmentMonth ||
              phonePaymentOptions?.minInstallments;
            const maxInstallmentMonth =
              getPhoneVariant?.maxInstallmentMonth ||
              phonePaymentOptions?.maxInstallments;
            const installmentStep =
              getPhoneVariant?.installmentStep ||
              phonePaymentOptions?.stepInstallments;
            const supportedInstallments = phonePaymentOptions?.supportedInstallments || [
              minInstallmentMonth,
              maxInstallmentMonth
            ];
            if (selectedPaymentTerm || paymentTerm) {
              const paymentMonth = selectedPaymentTerm || parseInt(paymentTerm);
              const value =
                paymentMonth >= maxInstallmentMonth
                  ? maxInstallmentMonth
                  : paymentMonth <= minInstallmentMonth
                  ? minInstallmentMonth
                  : paymentMonth / installmentStep === 0
                  ? paymentMonth
                  : round(paymentMonth / installmentStep) * installmentStep;
              const selectedTerm = includes(supportedInstallments, value)
                ? value
                : maxInstallmentMonth;
              setSelectedPhone("paymentTerm", selectedTerm);
            } else {
              setSelectedPhone("paymentTerm", maxInstallmentMonth);
            }
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasPhones,
    pvid,
    phoneFilter,
    selectedPlanName,
    eligiblePlans,
    paymentOption,
    selectedPaymentTerm,
    selectedPhoneVariant,
    planName
  ]);

  return (
    <IntlProvider locale="en" messages={messages}>
      {render({ loading: requiresFetching, requiresMaintenance })}
    </IntlProvider>
  );
};

const mapStateToProps = createStructuredSelector({
  messages: makeSelectMessages(),
  requiresFetching: makeSelectIsFetchingRequired(DEFAULT_KEYS_TO_FETCH),
  requiresMaintenance: makeSelectRequiresMaintenance(DEFAULT_KEYS_TO_FETCH),
  referralCode: makeSelectReferralCode(),
  customerInfo: makeSelectCustomerInfo(),
  planName: makeSelectPlanName(),
  phones: makeSelectPhones(),
  eligiblePlans: makeSelectEligiblePlans(),
  phonesForm: makeSelectPhonesForm(),
  channelPhonePaymentOptions: makeSelectChannelPhonePaymentOptions(),
  allowAccountNoQueryParam: makeSelectAllowAccountNoQueryParam(),
  workflowState: makeSelectWorkflowState()
});

const maintenanceOnError = (result, error) => {
  if (result?.maintenanceMode || !isEmpty(error)) {
    gotoUrl(result?.maintenancePage || paths.MAINTENANCE);
  }
};

export function mapDispatchToProps(dispatch) {
  return {
    initWorkflow: (channel, workflow) => {
      dispatch(setWorkflow({ channel, workflow }));
    },
    fetchData: params => {
      dispatch(fetchEligiblePlans(params, maintenanceOnError));
      dispatch(fetchAppSettings(maintenanceOnError));
      dispatch(fetchLocale(maintenanceOnError));
    },
    fetchPhones: () => {
      dispatch(fetchPhones());
    },
    fetchCustomerInfo: (account_number, ott, use_phone_wallet) =>
      dispatch(fetchCustomerInfo({ account_number, ott, use_phone_wallet })),
    validateReferralCode: (
      referral_code,
      plan_name,
      device_sku = null,
      orderRef = null
    ) => {
      const data = {
        referral_code,
        plan_name,
        ...(device_sku && { device_sku }),
        ...(orderRef && { orderRef })
      };
      dispatch(fetchReferralCode(data));
    },
    setSelectedPhone: (field, value) => {
      dispatch(
        changeFormField({
          form: forms.PHONES_FORM,
          field: field,
          value: value
        })
      );
    },
    selectPlan: plan => dispatch(selectPlan(plan)),
    setIccidParam: iccidparam =>
      dispatch(setData({ key: "iccIdInUrlParam", data: { iccidparam } }))
  };
}

DataLoader.defaultProps = {
  match: {},
  requiresFetching: false,
  messages: [],
  render: () => {}
};

const withConnect = connect(mapStateToProps, mapDispatchToProps);
export default compose(withConnect, memo)(DataLoader);
